/**
 * TypeScript interfaces for Device Flow Authentication
 * 
 * These interfaces define the data structures and contracts for implementing
 * OAuth 2.0 Device Authorization Grant flow with Amazon Federate using oidc-client-ts.
 */

import { getStage } from "../config/stage";

/**
 * User information extracted from authentication tokens
 */
export interface UserInfo {
  userAlias: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  siteLocation?: string;
  ldapGroups?: string[];
}

/**
 * JWT token set containing all authentication tokens
 */
export interface JWTTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

/**
 * Token set interface for managing authentication tokens
 */
export interface TokenSet {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
}

/**
 * Parameters for initiating device flow authentication
 */
export interface DeviceFlowInitiationParams {
  state?: string;
  clientRequestId?: string;
  redirectUri?: string;
}

/**
 * Response from successful token exchange
 */
export interface TokenResponse {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  userInfo: UserInfo;
}

/**
 * Result of authentication validation
 */
export interface AuthValidationResult {
  isValid: boolean;
  reason?: string;
  shouldClear: boolean;
}

/**
 * Result of authentication attempt
 */
export interface AuthResult {
  success: boolean;
  tokens?: TokenSet;
  userInfo?: UserInfo;
  error?: string;
}

/**
 * Device flow state for tracking authentication progress
 */
export interface DeviceFlowState {
  deviceCode: string;
  userCode: string;
  verificationUri: string;
  pollingInterval: number;
  expiresAt: number;
  status: 'pending' | 'authorized' | 'expired' | 'denied';
}

/**
 * Authentication context containing user information and auth type
 */
export interface UserContext {
  userAlias: string;
  isAdmin: boolean;
  authType: 'cognito' | 'federate';
  tokenExpiry: number;
  ldapGroups?: string[];
  firstName?: string;
  lastName?: string;
  email?: string;
  siteLocation?: string;
}

/**
 * Authentication manager interface
 */
export interface AuthManager {
  initializeAuth(): Promise<void>;
  authenticateUser(): Promise<AuthResult>;
  refreshTokens(): Promise<TokenSet>;
  logout(): Promise<void>;
  isAuthenticated(): boolean;
  getAuthType(): 'cognito' | 'federate' | null;
}

/**
 * Device flow handler interface using oidc-client-ts
 */
export interface DeviceFlowHandler {
  initiateDeviceFlow(params?: DeviceFlowInitiationParams): Promise<void>;
  handleCallback(authorizationCode: string): Promise<TokenResponse>;
  validateAuthenticationState(): Promise<AuthValidationResult>;
  refreshTokens(): Promise<JWTTokens>;
  clearInvalidAuthenticationState(): Promise<void>;
}

/**
 * Token manager interface for handling dual authentication types
 */
export interface TokenManager {
  storeTokens(tokens: TokenSet, type: 'cognito' | 'federate'): void;
  getTokens(type: 'cognito' | 'federate'): TokenSet | null;
  refreshAccessToken(type: 'cognito' | 'federate'): Promise<TokenSet>;
  clearTokens(type: 'cognito' | 'federate'): void;
  isTokenExpired(token: string): boolean;
  shouldRefreshToken(token: string): boolean;
  getStoredAuthType(): 'cognito' | 'federate' | null;
  ensureValidTokens(type: 'cognito' | 'federate'): Promise<TokenSet>;
}

/**
 * HTTP interceptor interface for authentication
 */
export interface AuthInterceptor {
  addAuthHeaders(request: any): any;
  handleAuthError(error: any): Promise<any>;
  retryWithRefresh(request: any): Promise<any>;
}

/**
 * Token storage keys for cognito authentication (existing)
 */
export const COGNITO_TOKEN_KEYS = {
  ACCESS_TOKEN: 'access_token',
  ID_TOKEN: 'id_token', 
  REFRESH_TOKEN: 'refresh_token'
} as const;

/**
 * Per-stage Federate profiles. Prod uses the dedicated WHS-EAP-SafetyGenie
 * profile on the Prod IdP; beta/gamma/local stay on the integ test profile.
 */
const FEDERATE_PROFILES = {
  prod: {
    AUTHORITY: "https://idp.federate.amazon.com",
    AUTHORIZE_ENDPOINT: "https://idp.federate.amazon.com/api/oauth2/v1/authorize",
    TOKEN_ENDPOINT: "https://idp.federate.amazon.com/api/oauth2/v2/token",
    CLIENT_ID: "WHS-EAP-SafetyGenie",
  },
  default: {
    AUTHORITY: "https://idp-integ.federate.amazon.com",
    AUTHORIZE_ENDPOINT: "https://idp-integ.federate.amazon.com/api/oauth2/v1/authorize",
    TOKEN_ENDPOINT: "https://idp-integ.federate.amazon.com/api/oauth2/v2/token",
    CLIENT_ID: "WHS-EAP-test-deep-link",
  },
} as const;

const activeFederateProfile = getStage() === "prod" ? FEDERATE_PROFILES.prod : FEDERATE_PROFILES.default;

/**
 * Federate configuration constants
 */
export const FEDERATE_CONFIG = {
  AUTHORIZE_ENDPOINT: activeFederateProfile.AUTHORIZE_ENDPOINT,
  TOKEN_ENDPOINT: activeFederateProfile.TOKEN_ENDPOINT,
  AUTHORITY: activeFederateProfile.AUTHORITY,
  CLIENT_ID: activeFederateProfile.CLIENT_ID,
  REDIRECT_URI: `${window.location.protocol}//${window.location.host}/device_callback`, // Dynamic based on current host
  SCOPES: ["openid", "profile", "email"],
  RESPONSE_TYPE: "code",
  PKCE_CODE_CHALLENGE_METHOD: "S256"
} as const;

/**
 * OIDC storage key for federate tokens
 * Format: oidc.user:{authority}:{client_id}
 * This is automatically generated from FEDERATE_CONFIG to stay in sync with deployment changes
 */
export const OIDC_STORAGE_KEY = `oidc.user:${FEDERATE_CONFIG.AUTHORITY}:${FEDERATE_CONFIG.CLIENT_ID}`;

/**
 * Authentication type enumeration
 */
export type AuthenticationType = 'cognito' | 'federate';

/**
 * Device type enumeration
 */
export type DeviceType = 'mobile' | 'desktop';
